import { useMemo, useState } from 'react';
import Modal from './Modal';
import Button from './Button';
import Badge from './Badge';

const ChevronIcon = () => (
    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
);

const CloseChipIcon = () => (
    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
);

function buildToolLookup(availableTools) {
    const map = new Map();
    for (const t of availableTools || []) {
        map.set(t.value, t);
    }
    return map;
}

export default function TagPicker({
    label,
    placeholder = 'Pick tags...',
    tags,
    availableTools,
    selected,
    onChange,
    modalTitle,
    modalDescription,
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [draft, setDraft] = useState(null);
    const selectedSet = useMemo(() => new Set(selected || []), [selected]);
    const toolLookup = useMemo(() => buildToolLookup(availableTools), [availableTools]);

    const openModal = () => {
        setDraft(new Set(selected || []));
        setQuery('');
        setOpen(true);
    };

    const closeModal = () => {
        setOpen(false);
        setDraft(null);
    };

    const applySelection = () => {
        onChange(Array.from(draft || []));
        closeModal();
    };

    const toggleTag = (tagName) => {
        if (!draft) return;
        const next = new Set(draft);
        if (next.has(tagName)) next.delete(tagName);
        else next.add(tagName);
        setDraft(next);
    };

    const removeTag = (tagName) => {
        onChange((selected || []).filter(t => t !== tagName));
    };

    const clearAll = () => onChange([]);

    const filteredTags = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return tags;
        return tags.filter(t =>
            t.label.toLowerCase().includes(q) ||
            (t.tools || []).some(toolName => toolName.toLowerCase().includes(q)),
        );
    }, [tags, query]);

    return (
        <div>
            {label && <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>}
            <button
                type="button"
                onClick={openModal}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 min-h-[38px] text-sm text-left bg-white border border-gray-300 rounded-md hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            >
                <div className="flex flex-wrap gap-1 flex-1">
                    {(selected && selected.length > 0) ? (
                        selected.map(tagName => (
                            <span
                                key={tagName}
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-md text-xs font-medium"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {tagName}
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); removeTag(tagName); }}
                                    className="hover:bg-blue-100 rounded-sm p-0.5"
                                >
                                    <CloseChipIcon />
                                </button>
                            </span>
                        ))
                    ) : (
                        <span className="text-gray-400">{placeholder}</span>
                    )}
                </div>
                <ChevronIcon />
            </button>

            <Modal
                open={open}
                onClose={closeModal}
                title={modalTitle || label || 'Select tags'}
                description={modalDescription || 'Browse tags and the tools under each.'}
                size="lg"
            >
                <div className="px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
                    <input
                        type="text"
                        placeholder="Search tags or tool names..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        autoFocus
                    />
                    <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                        <span>{filteredTags.length} tag(s) • {draft ? draft.size : 0} selected</span>
                        {draft && draft.size > 0 && (
                            <button
                                type="button"
                                onClick={() => setDraft(new Set())}
                                className="text-red-600 hover:underline"
                            >
                                Clear selection
                            </button>
                        )}
                    </div>
                </div>

                <div className="px-6 py-3 divide-y divide-gray-100">
                    {filteredTags.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-8">No tags match your search</p>
                    ) : filteredTags.map(tag => {
                        const checked = draft ? draft.has(tag.value) : false;
                        const toolItems = tag.tools || [];
                        return (
                            <div key={tag.value} className="py-3">
                                <label className="flex items-start gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggleTag(tag.value)}
                                        className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-gray-900">{tag.label}</span>
                                            <Badge variant="outline">{toolItems.length}</Badge>
                                        </div>
                                        {toolItems.length > 0 && (
                                            <div className="mt-1.5 flex flex-wrap gap-1">
                                                {toolItems.map(toolName => {
                                                    const info = toolLookup.get(toolName);
                                                    const isMcp = info?.is_mcp;
                                                    return (
                                                        <span
                                                            key={toolName}
                                                            className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-mono ${
                                                                isMcp ? 'bg-purple-50 text-purple-700' : 'bg-gray-100 text-gray-700'
                                                            }`}
                                                            title={info?.label || toolName}
                                                        >
                                                            {toolName}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </label>
                            </div>
                        );
                    })}
                </div>

                <Modal.Footer>
                    <Button variant="outline" size="sm" onClick={closeModal}>Cancel</Button>
                    <Button variant="primary" size="sm" onClick={applySelection}>
                        Apply {draft ? `(${draft.size})` : ''}
                    </Button>
                </Modal.Footer>
            </Modal>

            {selected && selected.length > 0 && (
                <div className="mt-1 text-right">
                    <button
                        type="button"
                        onClick={clearAll}
                        className="text-xs text-gray-500 hover:text-red-600"
                    >
                        Clear all
                    </button>
                </div>
            )}
        </div>
    );
}
